import { DEFAULT_TEAMS_SORT_BY } from "@components/teams/teams-constants";
import { SdbMemberTypes } from "@custom-types/member-types";
import { SortItem } from "@custom-types/sort-types";
import {
  SdbTeam,
  TeamsSortBy,
  TeamsSortField,
} from "@custom-types/teams-types";
import { FaroCoreApiTypes } from "@stellar/core-api-client";
import { formatUserRoleType } from "@utils/data-display";

/**
 * Gets the initials of a team name to be shown in an avatar
 * E.g. "Sphere Developers" -> "SD"
 *
 * @param teamName The name of a team
 * @returns The initials to show inside an avatar.
 */
export function getTeamAvatarInitials(teamName?: string | null): string {
  if (!teamName) {
    return "";
  }
  const teamNameWords = teamName.split(" ");

  // If the team name has two or more words, return the first letter of the first two words.
  if (teamNameWords.length >= 2) {
    return `${teamNameWords[0][0]}${teamNameWords[1][0]}`.toUpperCase();
  }

  // If the team name has one word, return the first two letters of the word
  return teamName.slice(0, 2).toUpperCase();
}

/**
 * Checks if the given email belongs to a special user group.
 *
 * This function validates the email against a set of predefined patterns.
 * If the email matches any of the patterns, it is considered an email belonging to a user group.
 *
 * @param email - The email address to be validated.
 * @returns true if the email matches any special user group patterns; otherwise, false.
 */
export function isTeam(
  member: SdbMemberTypes | FaroCoreApiTypes.UserAsManager
): boolean {
  // Check if the member has a "kind" property and if it's a team
  return member.kind === "user-group";
}

/**
 * Validates the team name
 * @returns true if the name contains comma or semicolon
 */
export function isValidTeamName(teamName: string): boolean {
  return (
    !teamName.includes(",") &&
    !teamName.includes(";") &&
    !teamName.includes("|")
  );
}

/** Sorts teams by the given sort criteria */
export function sortTeams(teams: SdbTeam[], sortBy: TeamsSortBy): SdbTeam[] {
  // Create a shallow copy of the teams array to avoid mutating the original array
  const sortedTeams = [...teams];

  // Pre-compute timestamps for creation date sorting to avoid repeated parsing
  const createdAtMap =
    sortBy === "creation_date_ascending" ||
    sortBy === "creation_date_descending"
      ? new Map(
          teams.map((team) => [team.id, new Date(team.createdAt).getTime()])
        )
      : null;

  sortedTeams.sort((a, b) => {
    switch (sortBy) {
      case "name_ascending":
        return a.name.localeCompare(b.name);
      case "name_descending":
        return b.name.localeCompare(a.name);
      case "users_ascending":
        return a.memberCount - b.memberCount;
      case "users_descending":
        return b.memberCount - a.memberCount;
      case "creation_date_ascending":
        return createdAtMap!.get(a.id)! - createdAtMap!.get(b.id)!;
      case "creation_date_descending":
        return createdAtMap!.get(b.id)! - createdAtMap!.get(a.id)!;
      case "workspace_role_ascending":
        return formatUserRoleType(a.role).localeCompare(
          formatUserRoleType(b.role)
        );
      case "workspace_role_descending":
        return formatUserRoleType(b.role).localeCompare(
          formatUserRoleType(a.role)
        );
      default:
        return 0;
    }
  });
  return sortedTeams;
}

/** Converts a SortItem to a TeamsSortBy value */
export function getTeamsSortBy(
  sortItem: SortItem<TeamsSortField>
): TeamsSortBy {
  const { field, sort } = sortItem;
  const isAscending = sort === "asc";
  switch (field) {
    case "name":
      return isAscending ? "name_ascending" : "name_descending";
    case "users":
      return isAscending ? "users_ascending" : "users_descending";
    case "creationDate":
      return isAscending
        ? "creation_date_ascending"
        : "creation_date_descending";
    case "workspaceRole":
      return isAscending
        ? "workspace_role_ascending"
        : "workspace_role_descending";
    default:
      return DEFAULT_TEAMS_SORT_BY;
  }
}
